import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Save, 
  Plus, 
  Trash2, 
  Edit, 
  Eye, 
  Clock, 
  Users, 
  BookOpen, 
  Video, 
  FileText, 
  CheckCircle2,
  AlertCircle,
  Calendar,
  Target,
  Award,
  Settings,
  Upload,
  X
} from 'lucide-react';
import { motion } from 'framer-motion';

// Mock data for form options
const mockData = {
  categories: [
    { id: 1, name: 'Safety' },
    { id: 2, name: 'Service' },
    { id: 3, name: 'Operations' },
    { id: 4, name: 'Management' },
    { id: 5, name: 'Technical' },
    { id: 6, name: 'Compliance' }
  ],
  levels: [
    { id: 1, name: 'Beginner' },
    { id: 2, name: 'Intermediate' },
    { id: 3, name: 'Advanced' },
    { id: 4, name: 'Expert' }
  ],
  instructors: [
    { id: 1, name: 'Sarah Johnson', email: 'sarah@company.com', department: 'Training' },
    { id: 2, name: 'Mike Chen', email: 'mike@company.com', department: 'Operations' },
    { id: 3, name: 'Lisa Rodriguez', email: 'lisa@company.com', department: 'Safety' },
    { id: 4, name: 'David Wilson', email: 'david@company.com', department: 'Management' }
  ],
  certificateTemplates: [
    { id: 1, name: 'Standard Certificate', description: 'Basic certificate template' },
    { id: 2, name: 'Premium Certificate', description: 'Advanced certificate with logo' },
    { id: 3, name: 'Custom Certificate', description: 'Fully customizable template' }
  ]
};

const CourseForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);

  // Form state
  const [formData, setFormData] = useState({
    // Basic Information
    title: '',
    description: '',
    category: '',
    level: '',
    instructor: '',
    duration: '',
    capacity: '',
    status: 'draft',
    
    // Course Content
    modules: [
      {
        id: 1,
        title: '',
        type: 'video',
        content: '',
        duration: '',
        order: 1
      }
    ],
    
    // Exam Settings
    hasExam: false,
    passingScore: 70,
    timeLimit: 60,
    allowRetakes: true,
    maxRetakes: 3,
    
    // Certificate Settings
    issueCertificate: true,
    certificateTemplate: '',
    certificateValidity: 12,
    
    // Scheduling
    startDate: '',
    endDate: '',
    enrollmentDeadline: '',
    
    // Additional Settings
    tags: [],
    prerequisites: [],
    learningObjectives: [],
    materials: []
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');

  // Load existing course data if editing
  useEffect(() => {
    if (isEditing) {
      // Mock existing course data
      const existingCourse = {
        title: 'Food Safety Fundamentals',
        description: 'Essential food safety practices for restaurant staff',
        category: 'Safety',
        level: 'Beginner',
        instructor: 'Sarah Johnson',
        duration: '2 hours',
        capacity: '50',
        status: 'active',
        modules: [
          {
            id: 1,
            title: 'Introduction to Food Safety',
            type: 'video',
            content: 'https://example.com/video1.mp4',
            duration: '15 minutes',
            order: 1
          },
          {
            id: 2,
            title: 'Hygiene Practices',
            type: 'document',
            content: 'hygiene-guidelines.pdf',
            duration: '20 minutes',
            order: 2
          },
          {
            id: 3,
            title: 'Food Safety Quiz',
            type: 'quiz',
            content: 'quiz-questions.json',
            duration: '30 minutes',
            order: 3
          }
        ],
        hasExam: true,
        passingScore: 80,
        timeLimit: 45,
        allowRetakes: true,
        maxRetakes: 2,
        issueCertificate: true,
        certificateTemplate: 'Standard Certificate',
        certificateValidity: 12,
        startDate: '2024-04-01',
        endDate: '2024-12-31',
        enrollmentDeadline: '2024-03-25',
        tags: ['food-safety', 'hygiene', 'compliance'],
        prerequisites: ['Basic English'],
        learningObjectives: [
          'Understand food safety principles',
          'Apply hygiene practices',
          'Identify food safety hazards'
        ],
        materials: ['Safety Manual', 'Video Tutorials', 'Practice Tests']
      };
      
      setFormData(existingCourse);
    }
  }, [isEditing, id]);

  const tabs = [
    { id: 'basic', label: 'Basic Information', icon: BookOpen },
    { id: 'content', label: 'Course Content', icon: FileText },
    { id: 'exam', label: 'Exam Settings', icon: Target },
    { id: 'certificate', label: 'Certificate', icon: Award },
    { id: 'schedule', label: 'Scheduling', icon: Calendar },
    { id: 'advanced', label: 'Advanced', icon: Settings }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleModuleChange = (moduleId, field, value) => {
    setFormData(prev => ({
      ...prev,
      modules: prev.modules.map(module =>
        module.id === moduleId ? { ...module, [field]: value } : module
      )
    }));
  };

  const addModule = () => {
    const newModule = {
      id: Date.now(),
      title: '',
      type: 'video',
      content: '',
      duration: '',
      order: formData.modules.length + 1
    };
    
    setFormData(prev => ({
      ...prev,
      modules: [...prev.modules, newModule]
    }));
  };

  const removeModule = (moduleId) => {
    setFormData(prev => ({
      ...prev,
      modules: prev.modules.filter(module => module.id !== moduleId)
    }));
  };

  const addTag = (tag) => {
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const addLearningObjective = (objective) => {
    if (objective && !formData.learningObjectives.includes(objective)) {
      setFormData(prev => ({
        ...prev,
        learningObjectives: [...prev.learningObjectives, objective]
      }));
    }
  };

  const removeLearningObjective = (objectiveToRemove) => {
    setFormData(prev => ({
      ...prev,
      learningObjectives: prev.learningObjectives.filter(obj => obj !== objectiveToRemove)
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Basic validation
    if (!formData.title.trim()) newErrors.title = 'Course title is required';
    if (!formData.description.trim()) newErrors.description = 'Course description is required';
    if (!formData.category) newErrors.category = 'Category is required';
    if (!formData.level) newErrors.level = 'Level is required';
    if (!formData.instructor) newErrors.instructor = 'Instructor is required';
    if (!formData.duration) newErrors.duration = 'Duration is required';

    // Module validation
    formData.modules.forEach((module, index) => {
      if (!module.title.trim()) {
        newErrors[`module_${index}_title`] = 'Module title is required';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      if (isEditing) {
        alert('Course updated successfully!');
      } else {
        alert('Course created successfully!');
      }
      
      navigate('/bloom');
    } catch (error) {
      alert('Error saving course. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const BasicInformationTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Course Title *
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
              errors.title ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Enter course title"
          />
          {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Category *
          </label>
          <select
            value={formData.category}
            onChange={(e) => handleInputChange('category', e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
              errors.category ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <option value="">Select category</option>
            {mockData.categories.map(category => (
              <option key={category.id} value={category.name}>
                {category.name}
              </option>
            ))}
          </select>
          {errors.category && <p className="mt-1 text-sm text-red-600">{errors.category}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Level *
          </label>
          <select
            value={formData.level}
            onChange={(e) => handleInputChange('level', e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
              errors.level ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <option value="">Select level</option>
            {mockData.levels.map(level => (
              <option key={level.id} value={level.name}>
                {level.name}
              </option>
            ))}
          </select>
          {errors.level && <p className="mt-1 text-sm text-red-600">{errors.level}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Instructor *
          </label>
          <select
            value={formData.instructor}
            onChange={(e) => handleInputChange('instructor', e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
              errors.instructor ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            <option value="">Select instructor</option>
            {mockData.instructors.map(instructor => (
              <option key={instructor.id} value={instructor.name}>
                {instructor.name} - {instructor.department}
              </option>
            ))}
          </select>
          {errors.instructor && <p className="mt-1 text-sm text-red-600">{errors.instructor}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Duration *
          </label>
          <input
            type="text"
            value={formData.duration}
            onChange={(e) => handleInputChange('duration', e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
              errors.duration ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="e.g., 2 hours"
          />
          {errors.duration && <p className="mt-1 text-sm text-red-600">{errors.duration}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Capacity
          </label>
          <input
            type="number"
            value={formData.capacity}
            onChange={(e) => handleInputChange('capacity', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            placeholder="Maximum number of learners"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Description *
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          rows={4}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
            errors.description ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="Describe the course content and objectives"
        />
        {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Status
        </label>
        <select
          value={formData.status}
          onChange={(e) => handleInputChange('status', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        >
          <option value="draft">Draft</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="archived">Archived</option>
        </select>
      </div>
    </div>
  );

  const CourseContentTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">Course Modules</h3>
        <button
          type="button"
          onClick={addModule}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Module
        </button>
      </div>

      <div className="space-y-4">
        {formData.modules.map((module, index) => (
          <motion.div
            key={module.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gray-50 rounded-lg p-6"
          >
            <div className="flex justify-between items-start mb-4">
              <h4 className="text-md font-medium text-gray-900">Module {module.order}</h4>
              <button
                type="button"
                onClick={() => removeModule(module.id)}
                className="p-1 text-red-600 hover:bg-red-50 rounded"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Module Title *
                </label>
                <input
                  type="text"
                  value={module.title}
                  onChange={(e) => handleModuleChange(module.id, 'title', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                    errors[`module_${index}_title`] ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter module title"
                />
                {errors[`module_${index}_title`] && (
                  <p className="mt-1 text-sm text-red-600">{errors[`module_${index}_title`]}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Module Type
                </label>
                <select
                  value={module.type}
                  onChange={(e) => handleModuleChange(module.id, 'type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="video">Video</option>
                  <option value="document">Document</option>
                  <option value="quiz">Quiz</option>
                  <option value="assignment">Assignment</option>
                  <option value="interactive">Interactive</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Content
                </label>
                <input
                  type="text"
                  value={module.content}
                  onChange={(e) => handleModuleChange(module.id, 'content', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder={module.type === 'video' ? 'Video URL' : 'File path or content'}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Duration
                </label>
                <input
                  type="text"
                  value={module.duration}
                  onChange={(e) => handleModuleChange(module.id, 'duration', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="e.g., 15 minutes"
                />
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );

  const ExamSettingsTab = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          id="hasExam"
          checked={formData.hasExam}
          onChange={(e) => handleInputChange('hasExam', e.target.checked)}
          className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
        />
        <label htmlFor="hasExam" className="text-sm font-medium text-gray-700">
          Include final exam
        </label>
      </div>

      {formData.hasExam && (
        <div className="space-y-6 pl-7">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Passing Score (%)
              </label>
              <input
                type="number"
                value={formData.passingScore}
                onChange={(e) => handleInputChange('passingScore', parseInt(e.target.value))}
                min="0"
                max="100"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Time Limit (minutes)
              </label>
              <input
                type="number"
                value={formData.timeLimit}
                onChange={(e) => handleInputChange('timeLimit', parseInt(e.target.value))}
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="allowRetakes"
              checked={formData.allowRetakes}
              onChange={(e) => handleInputChange('allowRetakes', e.target.checked)}
              className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
            />
            <label htmlFor="allowRetakes" className="text-sm font-medium text-gray-700">
              Allow retakes
            </label>
          </div>

          {formData.allowRetakes && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Maximum Retakes
              </label>
              <input
                type="number"
                value={formData.maxRetakes}
                onChange={(e) => handleInputChange('maxRetakes', parseInt(e.target.value))}
                min="1"
                max="10"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );

  const CertificateTab = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          id="issueCertificate"
          checked={formData.issueCertificate}
          onChange={(e) => handleInputChange('issueCertificate', e.target.checked)}
          className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
        />
        <label htmlFor="issueCertificate" className="text-sm font-medium text-gray-700">
          Issue certificate upon completion
        </label>
      </div>

      {formData.issueCertificate && (
        <div className="space-y-6 pl-7">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Certificate Template
            </label>
            <select
              value={formData.certificateTemplate}
              onChange={(e) => handleInputChange('certificateTemplate', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">Select template</option>
              {mockData.certificateTemplates.map(template => (
                <option key={template.id} value={template.name}>
                  {template.name} - {template.description}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Certificate Validity (months)
            </label>
            <input
              type="number"
              value={formData.certificateValidity}
              onChange={(e) => handleInputChange('certificateValidity', parseInt(e.target.value))}
              min="1"
              max="60"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
        </div>
      )}
    </div>
  );

  const SchedulingTab = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Start Date
          </label>
          <input
            type="date"
            value={formData.startDate}
            onChange={(e) => handleInputChange('startDate', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            End Date
          </label>
          <input
            type="date"
            value={formData.endDate}
            onChange={(e) => handleInputChange('endDate', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Enrollment Deadline
          </label>
          <input
            type="date"
            value={formData.enrollmentDeadline}
            onChange={(e) => handleInputChange('enrollmentDeadline', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>
      </div>
    </div>
  );

  const AdvancedTab = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Tags
        </label>
        <div className="flex flex-wrap gap-2 mb-2">
          {formData.tags.map((tag, index) => (
            <span
              key={index}
              className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm"
            >
              {tag}
              <button
                type="button"
                onClick={() => removeTag(tag)}
                className="text-purple-600 hover:text-purple-800"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Add tag"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addTag(e.target.value);
                e.target.value = '';
              }
            }}
          />
          <button
            type="button"
            onClick={(e) => {
              const input = e.target.previousSibling;
              addTag(input.value);
              input.value = '';
            }}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            Add
          </button>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Learning Objectives
        </label>
        <div className="space-y-2 mb-2">
          {formData.learningObjectives.map((objective, index) => (
            <div key={index} className="flex items-center gap-2">
              <span className="w-2 h-2 bg-purple-600 rounded-full"></span>
              <span className="flex-1">{objective}</span>
              <button
                type="button"
                onClick={() => removeLearningObjective(objective)}
                className="text-red-600 hover:text-red-800"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Add learning objective"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addLearningObjective(e.target.value);
                e.target.value = '';
              }
            }}
          />
          <button
            type="button"
            onClick={(e) => {
              const input = e.target.previousSibling;
              addLearningObjective(input.value);
              input.value = '';
            }}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            Add
          </button>
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic':
        return <BasicInformationTab />;
      case 'content':
        return <CourseContentTab />;
      case 'exam':
        return <ExamSettingsTab />;
      case 'certificate':
        return <CertificateTab />;
      case 'schedule':
        return <SchedulingTab />;
      case 'advanced':
        return <AdvancedTab />;
      default:
        return <BasicInformationTab />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-4">
          <Link
            to="/bloom"
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Bloom
          </Link>
        </div>
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isEditing ? 'Edit Course' : 'Create New Course'}
            </h1>
            <p className="text-gray-500">
              {isEditing ? 'Update course information and settings' : 'Set up a new learning course'}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => navigate('/bloom')}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="courseForm"
              disabled={isSubmitting}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {isEditing ? 'Update Course' : 'Create Course'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <form id="courseForm" onSubmit={handleSubmit}>
        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-purple-500 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {renderTabContent()}
          </motion.div>
        </div>
      </form>
    </div>
  );
};

export default CourseForm;
